import React, { useEffect, useState } from 'react';
import SettingsEmptyState from '../settings/SettingsEmptyState';
import useFetch from '../../hooks/useFetch';
import useDelete from '../../hooks/useDelete';
import { useTranslation } from 'react-i18next';
import { MwButton, MwSpinnerButton, MwToast } from '../ui';
import DeleteConfirmationModal from '../ui/DeleteConfirmationModal';
import { useCurrent } from '../../hooks';
import AddEditCurrencyModal from './CurrencyComponent/AddEditCurrencyModal';
import CurrencyListItem from './CurrencyComponent/CurrencyListItem';
import { FaMoneyBillWave } from 'react-icons/fa';

const CurrencyPage = () => {
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;
  
  const { currentTenantId, currentCompanyId } = useCurrent();
  const baseUrl = `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/currencies/${currentTenantId}/${currentCompanyId}`;

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [mode, setMode] = useState('add');
  const [currentUrl, setCurrentUrl] = useState(baseUrl);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  
  const { data, loading, refreshHandler } = useFetch(currentUrl);
  const { deleteItem, loading: deleteLoading } = useDelete();
  
  useEffect(() => {
    setCurrentUrl(baseUrl);
  }, [baseUrl]);
  
  const addHandler = () => {
    setMode('add');
    setSelectedItemId(null);
    setModalOpen(true);
  };
  
  const editHandler = (id) => {
    setMode('edit');
    setSelectedItemId(id);
    setModalOpen(true);
  };
  
  const deleteHandler = async () => {
    const deleteUrl = `${process.env.REACT_APP_INV_API_SERVER_BASE_URL}/currencies/delete/${selectedItemId}`;
    try {
      await deleteItem(deleteUrl);
      setToastMessage(t('currency.deleteSuccess', 'تم حذف العملة بنجاح'));
      setShowToast(true);
    } catch (error) {
      setToastMessage(t('currency.deleteError', 'حدث خطأ أثناء الحذف'));
      setShowToast(true);
      console.log(`Error deleting: ${error}`);
    }
    refreshHandler();
    setDeleteModalOpen(false);
  };
  
  const paginateHandler = (url) => {
    setCurrentUrl(url.replace('http', 'https'));
  };

  const decodeLabel = (label) => {
    if (!label) return '';
    if (typeof window === 'undefined' || !window.DOMParser) {
      return label.replace(/<[^>]+>/g, '');
    }
    const doc = new DOMParser().parseFromString(label, 'text/html');
    return doc.body.textContent || '';
  };
  
  return (
    <>
      <MwToast msg={toastMessage} isShow={showToast} setIsShow={setShowToast} />
      <div className="flex justify-between items-center mb-4">
        <p className="font-bold text-gray-500 text-sm">{t('currency.title', 'العملات')}</p>
        <MwButton
          onClick={addHandler}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          type='saveBtn'
        >
          <FaMoneyBillWave size={15} />
          {t('currency.add', 'إضافة عملة')}
        </MwButton>
      </div>
      
      <div className='flex-1 flex flex-col'>
        {loading && <MwSpinnerButton withLabel={false}/>}
        {
          data?.data?.length > 0 ?
          <div className='w-full text-xs rounded-xl text-gray-500'>
            <div className='flex gap-2 font-bold px-3 py-2 rounded-t-xl justify-between items-center'>
              <div className='w-16 text-center'>#</div>
              <div className='flex-1'>{t('currency.name', 'الاسم')}</div>
              <div className='w-36 text-center'>{t('currency.code', 'الرمز')}</div>
              <div className='w-36 text-center'>{t('currency.rate', 'السعر')}</div>
              <div className='w-36 text-center'>{t('currency.status', 'الحالة')}</div>
              <div className='w-36 text-center'>{t('currency.actions', 'الإجراءات')}</div>
            </div>
            {
              data?.data.map((item, index) => (
                <CurrencyListItem 
                  key={item.id} 
                  index={index} 
                  item={item} 
                  lang={lang}
                  isRTL={isRTL}
                  editHandler={editHandler}
                  setSelectedItemId={setSelectedItemId}
                  setDeleteModalOpen={setDeleteModalOpen}
                />
              ))
            }
            {data?.links && data?.links.length > 0 &&
              <div 
                id="dataLinks" 
                className="list-none flex gap-3 border-t py-3 px-5 cursor-default"
              >
                {
                  data?.data?.length > 3 && 
                  data?.links.map((item, i) => (
                    <li 
                      key={i} 
                      className='hover:font-bold' 
                      onClick={() => item?.url && paginateHandler(item?.url)}
                    >
                      {decodeLabel(item?.label)}
                    </li>
                  ))
                }
              </div>
            }
          </div> 
          : 
          <SettingsEmptyState msg={t('currency.noData', 'لا توجد عملات متاحة')} />
        }
      </div>
      
      {modalOpen && (
        <AddEditCurrencyModal 
          isOpen={modalOpen} 
          onClose={() => setModalOpen(false)} 
          mode={mode} 
          itemId={selectedItemId} 
          onSuccess={refreshHandler}
          baseUrl={baseUrl}
        />
      )}
      
      {deleteModalOpen && (
        <DeleteConfirmationModal 
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={deleteHandler}
          title={t('currency.deleteConfirmTitle', 'تأكيد الحذف')}
          message={t('currency.deleteConfirmMessage', 'هل أنت متأكد من حذف هذه العملة؟')}
          loading={deleteLoading}
        />
      )}
    </>
  );
};

export default CurrencyPage;
